import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ExcelJS from 'exceljs';
import AdminIcon from '../components/admin/AdminIcon';
import RecordsPagination from '../components/admin/RecordsPagination';
import { collectExportRecords, exportCellValue, fetchDocument, recordsQuery, saveDownload, type ExportMode } from '../lib/records-export';
import { adminLogout, deleteAdminSubmission, getAdminSubmission, getScoreStats, getSurvey, listAdminSubmissions, listSurveys, purgeSurveySubmissions, type SubmissionRecord } from '../lib/api';
import { titleText, OPTION_TEXT_KEY, optionTextsOf, decorateChoiceAnswer, rankingText, rankingIndexText, matrixTextColumnsOf, matrixTextCellValue, matrixTextRowText } from '../components/editor/types';
import { displayIndexMap } from '../components/editor/question-utils';
import { listSurveyFolders, type SurveyFolderList } from '../lib/api';
import { folderPath, folderScope, inFolder, UNFILED } from '../lib/survey-folders';

type Props = { onLogout: () => void };
type QuestionMeta = { title: string; type: string; rows?: string[]; optionValues?: string[]; textColumns?: { title: string; kind: string }[]; displayIndex?: number };
type SurveyMeta = { questions: Record<string, QuestionMeta>; order: string[] };
function surveyMeta(config: any): SurveyMeta {
  const questions = [...(config?.questions ?? []), ...(config?.settings?.preface?.questions ?? [])];
  const indices = displayIndexMap(questions);
  return {
    order: questions.map((q) => q.id),
    questions: Object.fromEntries(questions.map((q) => [q.id, {
      title: titleText(q.title) || q.id, type: q.type, rows: q.params?.rows,
      optionValues: Array.isArray(q.options) ? q.options.map((o: any) => typeof o === 'string' ? o : o?.value).filter((v: any) => typeof v === 'string') : undefined,
      textColumns: q.type === 'matrixtext' ? matrixTextColumnsOf(q).map((c) => ({ title: c.title, kind: c.kind ?? 'text' })) : undefined,
      displayIndex: indices.get(q.id),
    }])),
  };
}
const actionError = (error: any) => error?.status === 401 ? '登录已过期，请刷新页面并重新登录。' : error?.body?.message ?? error?.message ?? '网络异常，请重试。';
const dateText = (value: number | null) => value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';
const pad2 = (n: number) => String(n).padStart(2, '0');
/** 导出用时间：固定 yyyy-MM-dd HH:mm:ss，避免 Excel 当日期换格式 */
const csvDate = (value: number | null) => {
  if (!value) return '-';
  const d = new Date(value);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};
const answerText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  // 0904 多图：附件题 value 是数组 [{ attachment:true, name, type, size }]，逐项展开
  if (Array.isArray(value)) {
    // 多选/排序题答案是字符串数组 → join；附件题数组每个是对象 → 走 answerText 拼接
    if (value.length > 0 && typeof value[0] === 'object' && (value[0] as any).attachment === true) {
      return value.map((x) => `[附件] ${(x as any).name ?? ''}`).join('、');
    }
    return value.map((x) => String(x)).join('、');
  }
  if (typeof value === 'object') {
    const obj = value as any;
    if (obj.attachment === true) return `[附件] ${obj.name ?? ''}`;
    if (typeof obj.text === 'string' || Array.isArray(obj.images)) {
      const n = Array.isArray(obj.images) ? obj.images.length : 0;
      const t = String(obj.text ?? '').trim();
      return `${t || (n ? '' : '-')}${n ? `${t ? ' ' : ''}（图片${n}张）` : ''}`; // 文本题答案配图
    }
    // 矩阵量表 / 矩阵填空 / 矩阵滑动逐行（0907 起矩阵填空的行值可为 { 列: 文本 }）
    return Object.entries(obj).map(([r, c]) => `${r}：${matrixTextRowText(c)}`).join('\n');
  }
  return String(value);
};
/** 加载 data URL 图片拿原尺寸，按 maxPx 等比缩放（不放大），失败返回 null */
function loadSignatureSize(sig: string | null | undefined, maxPx: number): Promise<{ w: number; h: number } | null> {
  if (!sig || !/^data:image\//.test(sig)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxPx / img.naturalWidth, maxPx / img.naturalHeight, 1);
      resolve({ w: Math.max(1, Math.round(img.naturalWidth * ratio)), h: Math.max(1, Math.round(img.naturalHeight * ratio)) });
    };
    img.onerror = () => resolve(null);
    img.src = sig;
  });
}
/** 加载 data URL 图片拿原尺寸，按 maxW/maxH 等比缩放（不放大），失败返回 null */
function loadSignatureSizeConstrained(sig: string | null | undefined, maxW: number, maxH: number): Promise<{ w: number; h: number } | null> {
  if (!sig || !/^data:image\//.test(sig)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
      resolve({ w: Math.max(1, Math.round(img.naturalWidth * ratio)), h: Math.max(1, Math.round(img.naturalHeight * ratio)) });
    };
    img.onerror = () => resolve(null);
    img.src = sig;
  });
}

export default function DataRecords({ onLogout }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folder') ?? '';
  const [folderData, setFolderData] = useState<SurveyFolderList>({ items: [], memberships: [] });
  const [records, setRecords] = useState<SubmissionRecord[]>([]);
  const [surveys, setSurveys] = useState<any[]>([]);
  const [surveysReady, setSurveysReady] = useState(false);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [surveyId, setSurveyId] = useState(() => searchParams.get('survey') ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const loadRequest = useRef(0);
  const [selected, setSelected] = useState<SubmissionRecord | null>(null);
  const [qTitles, setQTitles] = useState<Record<string, Record<string, string>>>({});
  const [confirmDel, setConfirmDel] = useState(false); // 详情弹窗里的两步删除确认
  const [purgeOpen, setPurgeOpen] = useState(false);   // 清空问卷数据确认弹窗
  const [exportMenuOpen, setExportMenuOpen] = useState(false); // 导出按钮旁的 ▽ 下拉（0903 改造）
  const exportMenuRef = useRef<HTMLDivElement | null>(null);   // 点击外部关闭
  const [busy, setBusy] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const exportRunning = useRef(false);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const detailRequest = useRef(0);
  const pageSize = 12;

  async function removeSelected() {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await deleteAdminSubmission(selected.id);
      setSelected(null);
      setConfirmDel(false);
      await load();
    } catch (e) {
      window.alert(`删除失败：${actionError(e)}`);
    } finally { setBusy(false); }
  }

  async function purgeCurrentSurvey() {
    if (!surveyId || busy) return;
    setBusy(true);
    try {
      await purgeSurveySubmissions(surveyId);
      setPurgeOpen(false);
      setPage(0);
      await load(0);
    } catch (e: any) {
      window.alert(`清空失败（${e?.status ?? '网络错误'}）：${e?.body?.message ?? e?.body?.error ?? e?.message ?? '未知错误'}`);
    } finally { setBusy(false); }
  }

  async function load(targetPage = page) {
    if (!surveysReady) return;
    const request = ++loadRequest.current;
    if (!surveyId) {
      setRecords([]); setTotal(0); setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const result = await listAdminSubmissions({ ...recordsQuery(surveyId, appliedKeyword, from, to), limit: pageSize, offset: targetPage * pageSize });
      if (request !== loadRequest.current) return;
      const lastPage = Math.max(0, Math.ceil(result.total / pageSize) - 1);
      if (targetPage > lastPage) {
        setRecords([]); setTotal(result.total); setPage(lastPage);
        return;
      }
      setRecords(result.items); setTotal(result.total);
    } catch (e) {
      if (request !== loadRequest.current) return;
      setRecords([]); setTotal(0); setLoadError(from && to && from > to ? actionError(e) : '提交记录加载失败，请重试。');
    } finally { if (request === loadRequest.current) setLoading(false); }
  }
  useEffect(() => {
    let cancelled = false;
    setSurveysReady(false); setLoading(true); setLoadError(''); setRecords([]); setTotal(0);
    loadRequest.current += 1;
    Promise.all([listSurveys(), listSurveyFolders()]).then(([data, folders]) => {
      if (cancelled) return;
      setFolderData(folders);
      const memberships = new Map(folders.memberships.map(m => [m.surveyId, m.folderId]));
      const scope = folderScope(folders.items, folderId);
      const items = data.items.filter(s => inFolder(folderId, memberships.get(s.id), scope));
      setSurveys(items);
      const requested = searchParams.get('survey');
      setSurveyId((current) => items.some((s) => s.id === requested) ? requested! : items.some((s) => s.id === current) ? current : (items[0]?.id ?? ''));
      setPage(0);
      setSurveysReady(true);
    }).catch(() => {
      if (!cancelled) { setLoadError('问卷列表加载失败，请刷新页面重试。'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [searchParams]);
  useEffect(() => {
    void load();
    return () => { loadRequest.current += 1; };
  }, [surveysReady, page, surveyId, from, to, appliedKeyword]);

  function searchRecords() {
    if (appliedKeyword !== keyword.trim()) { setAppliedKeyword(keyword.trim()); setPage(0); }
    else if (page === 0) void load(0);
    else setPage(0);
  }

  useEffect(() => {
    setSelected(null); setDetailLoading(null);
    return () => { detailRequest.current += 1; };
  }, [surveyId]);

  async function showDetail(id: number) {
    const request = ++detailRequest.current;
    setDetailLoading(id); setConfirmDel(false);
    try {
      const data = await getAdminSubmission(id);
      if (request === detailRequest.current) setSelected(data.submission);
    } catch (e) {
      if (request === detailRequest.current) window.alert(`详情加载失败：${actionError(e)}`);
    } finally {
      if (request === detailRequest.current) setDetailLoading(null);
    }
  }

  async function downloadDocuments() {
    if (!surveyId || documentBusy) return;
    setDocumentBusy(true);
    try {
      const response = await fetchDocument(`/api/admin/documents.zip?surveyId=${encodeURIComponent(surveyId)}`);
      saveDownload(await response.blob(), `签署文档-${surveyId}-${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (e) { window.alert(actionError(e)); }
    finally { setDocumentBusy(false); }
  }

  async function openDocument(download: boolean) {
    if (!selected || documentBusy) return;
    const popup = download ? null : window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    setDocumentBusy(true);
    try {
      const response = await fetchDocument(`/api/submissions/${encodeURIComponent(selected.editToken)}/document`);
      const blob = await response.blob();
      if (download || !popup) saveDownload(blob, `签署文档-序号${selected.sequence ?? selected.id}.pdf`);
      else {
        const url = URL.createObjectURL(blob);
        popup.location.href = url;
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (e) { popup?.close(); window.alert(actionError(e)); }
    finally { setDocumentBusy(false); }
  }

  // 拉取记录涉及问卷的题目元信息（列头/导出显示题干；矩阵导出拆行；选择题导出按 index 时也用）
  // 选项值数组是「按序号下载」的关键：A/B/C/D 等选项的"第 N 个"被映射成数字 N（0903 改造）
  const [qMeta, setQMeta] = useState<Record<string, SurveyMeta>>({});
  useEffect(() => {
    const ids = Array.from(new Set(records.map((r) => r.surveyId))).filter((id) => !qTitles[id]);
    if (!ids.length) return;
    Promise.all(ids.map((id) => getSurvey(id).then((d) => {
      const meta = surveyMeta(d.config);
      return [id, Object.fromEntries(Object.entries(meta.questions).map(([key, q]) => [key, q.title])), meta] as const;
    }).catch(() => null)))
      .then((triples) => {
        const loaded = triples.filter((item): item is NonNullable<typeof item> => item !== null);
        setQTitles((prev) => ({ ...prev, ...Object.fromEntries(loaded.map(([id, t]) => [id, t])) }));
        setQMeta((prev) => ({ ...prev, ...Object.fromEntries(loaded.map(([id, , m]) => [id, m])) }));
      });
  }, [records]);

  // 评分统计（选定问卷且有计分提交时显示）
  const [scoreStats, setScoreStats] = useState<{ total: number; scored: number; avg: number | null; min: number | null; max: number | null } | null>(null);
  useEffect(() => {
    let cancelled = false;
    setScoreStats(null);
    if (!surveyId) { setScoreStats(null); return; }
    getScoreStats(surveyId).then((data) => { if (!cancelled) setScoreStats(data); }).catch(() => { if (!cancelled) setScoreStats(null); });
    return () => { cancelled = true; };
  }, [surveyId, records]);

  // 导出 ▽ 下拉：点外面/按 Esc/点菜单项后自动关闭
  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDocDown = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) setExportMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExportMenuOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocDown); document.removeEventListener('keydown', onKey); };
  }, [exportMenuOpen]);

  const columns = useMemo(() => {
    // _optionText 是选项附属填空的保留键（0902），不是题目列
    const keys = new Set<string>(); records.forEach((record) => Object.keys(record.answers).forEach((key) => { if (key !== OPTION_TEXT_KEY) keys.add(key); }));
    return Array.from(keys).slice(0, 4);
  }, [records]);

  const surveyTitle = (id: string) => surveys.find((s) => s.id === id)?.mainTitle ?? id;
  /** 列头题干：优先当前筛选的问卷，其次任一含该题号的问卷 */
  const titleFor = (key: string): string => {
    if (surveyId && qTitles[surveyId]?.[key]) return qTitles[surveyId][key];
    for (const id of Object.keys(qTitles)) if (qTitles[id][key]) return qTitles[id][key];
    return key;
  };

  /** 某题在导出里的元信息（跨问卷时取第一个命中的） */
  const metaFor = (key: string) => {
    if (surveyId && qMeta[surveyId]?.questions[key]) return qMeta[surveyId].questions[key];
    for (const id of Object.keys(qMeta)) if (qMeta[id].questions[key]) return qMeta[id].questions[key];
    return null;
  };

  /** 0902 选项附属填空：选择题答案拼上填写文本 → "其他：xxx"（没有填空文本时原样返回） */
  const shownValue = (answers: Record<string, any>, key: string): unknown => decorateChoiceAnswer(answers[key], optionTextsOf(answers, key));
  /** 列表 / 详情里的单格文本：排序题（0903）按名次 "A,B,C"，其它走 answerText */
  const cellText = (answers: Record<string, any>, key: string): string =>
    metaFor(key)?.type === 'ranking' ? (rankingText(answers[key]) || '-') : answerText(shownValue(answers, key));

  /** 某题答案按 mode 输出为单格字符串
 * - mode='signed'|'text'（按文本）：选择题原文输出（保留 _optionText 附属填空）；其它题型走 answerText
 * - mode='number'（按序号）：选择题按选项位置映射成数字（1/2/3/4）；未在选项列表里的值原样保留（排查历史脏数据）；多选用"、"分隔
 * 矩阵题在 exportXls 里按行拆，不走这里
 */
  function formatAnswer(answers: Record<string, any>, key: string, mode: ExportMode, rankingOption?: string, meta = metaFor(key)): string {
    const value = answers[key];
    // 排序题（0903）：按名次 → 分隔；按序号模式输出选项位置 "2,1,3"
    // 宽表分支（rankingOption 有值）：单格 = 该候选项在排序结果中的名次（index+1），未排到留空（0908 改造）
    if (meta?.type === 'ranking') {
      if (!Array.isArray(value) || !value.length) return '-';
      if (rankingOption !== undefined) {
        const i = value.indexOf(rankingOption);
        return i >= 0 ? String(i + 1) : '';
      }
      return mode === 'number' && Array.isArray(meta.optionValues) ? rankingIndexText(meta.optionValues, value) : rankingText(value);
    }
    if (meta && (meta.type === 'single' || meta.type === 'multi' || meta.type === 'select') && Array.isArray(meta.optionValues) && meta.optionValues.length) {
      const opts = meta.optionValues;
      const idxOf = (v: string) => { const i = opts.indexOf(v); return i >= 0 ? i + 1 : NaN; };
      if (mode === 'number') {
        // 多选 → 数组；单选/select → 单值；空值 → '-'
        if (Array.isArray(value)) {
          return value.length ? value.map((v) => { const n = idxOf(String(v)); return Number.isNaN(n) ? String(v) : String(n); }).join('、') : '-';
        }
        if (value === undefined || value === null || value === '') return '-';
        const n = idxOf(String(value));
        return Number.isNaN(n) ? String(value) : String(n);
      }
      // text 模式：选择题走 shownValue（带附属填空装饰），其它走 answerText
      return answerText(shownValue(answers, key));
    }
    return answerText(value);
  }

  async function exportXls(mode: ExportMode = 'signed') {
    if (exportRunning.current || loading || !surveyId || !total) return;
    exportRunning.current = true;
    setExportMenuOpen(false);
    setExportProgress('正在准备导出…');
    try {
      // 先取得题目定义，避免元信息失败后遗留的分页请求继续改写导出进度。
      const survey = await getSurvey(surveyId);
      const exportRecords = await collectExportRecords(recordsQuery(surveyId, appliedKeyword, from, to), (loaded, count) => setExportProgress(`正在导出 ${loaded}/${count} 条…`));
      if (!exportRecords.length) throw new Error('当前筛选条件下没有记录，请重新查询。');
      const exportMeta = surveyMeta(survey.config);
      const metaFor = (key: string) => exportMeta.questions[key] ?? null;
      // 导出用全量题目列（不受表格 4 列截断限制）；矩阵题按行拆成多列便于统计
      // 0908 列顺序按问卷题目排：先按 survey 出现顺序，再按每个 survey 的 questionsOrder，让"1、个人信息"在最左
      const keySet = new Set<string>();
      exportRecords.forEach((r) => Object.keys(r.answers).forEach((k) => { if (k !== OPTION_TEXT_KEY) keySet.add(k); }));
      const seenKey = new Set<string>();
      const allKeys: string[] = [];
      const seenSurvey = new Set<string>();
      for (const r of exportRecords) {
        if (seenSurvey.has(r.surveyId)) continue;
        seenSurvey.add(r.surveyId);
        const order = exportMeta.order;
        if (Array.isArray(order)) {
          for (const k of order) {
            if (keySet.has(k) && !seenKey.has(k)) { allKeys.push(k); seenKey.add(k); }
          }
        }
      }
      // 兜底：剩余（不在 questionsOrder 里 / 跨 survey）按字母序追加
      Array.from(keySet).filter((k) => !seenKey.has(k)).sort().forEach((k) => { allKeys.push(k); seenKey.add(k); });
      const exportCols: Array<{ key: string; row?: string; col?: string; colIndex?: number; label: string; rankingOption?: string }> = [];
      for (const key of allKeys) {
        const meta = metaFor(key);
        // 0908 列头加题号：所有列都带 `{题号}、` 前缀（含排序题宽表 1128 改造）
        const prefix = meta?.displayIndex ? `${meta.displayIndex}、` : '';
        if ((meta?.type === 'matrix' || meta?.type === 'matrixtext' || meta?.type === 'matrixslider') && Array.isArray(meta.rows) && meta.rows.length) {
          const cols = meta.type === 'matrixtext' && Array.isArray(meta.textColumns) && meta.textColumns.length ? meta.textColumns : null;
          meta.rows.forEach((row) => {
            if (cols) cols.forEach((c, colIndex) => exportCols.push({ key, row, col: c.title, colIndex, label: `${prefix}${meta.title}·${row}·${c.title}` }));
            else exportCols.push({ key, row, label: `${prefix}${meta.title}·${row}` });
          });
        } else if (meta?.type === 'ranking' && mode === 'number' && Array.isArray(meta.optionValues) && meta.optionValues.length) {
          // 排序题按序号导出 → 宽表：每个候选项一列，列名 = `{题号}、{值}`（1128 改造加题号；之前只显示值）
          // 单元格 = 该候选项在填答者排序结果中的名次（index+1）；未排到留空。
          meta.optionValues.forEach((optVal) => {
            exportCols.push({ key, rankingOption: optVal, label: `${prefix}${optVal}` });
          });
        } else {
          exportCols.push({ key, label: `${prefix}${meta?.title ?? key}` });
        }
      }
      const hasScore = exportRecords.some((r) => r.score !== null && r.score !== undefined);
      // 三种模式都嵌入签名图（0903 调整：用户要求"都加上电子签名图"）；只要任一记录有签名就加列
      const hasSignature = exportRecords.some((r) => !!r.signature);
      // 单元格固定尺寸：列宽 22 字符（约 154px），行高 54pt（约 72px，容纳 40px 高图 + padding）
      // 0908+ 把列宽从 20→22、行高从 50→54，给图片更多 padding 防越界
      const SIG_COL_WIDTH_CHARS = 22;
      const SIG_ROW_HEIGHT_PT = 54;
      // 单图约束：高 ≤ 40px（行高 72px 的 ~55%），宽 ≤ 单元格宽 * 0.8（双保险防越界）
      const SIG_MAX_H_PX = 40;
      const SIG_MAX_W_PX = Math.floor(SIG_COL_WIDTH_CHARS * 7 * 0.8); // ≈ 123px
      // EMU 换算：OOXML 标准下 1px = 9525 EMU。ExcelJS 的 `tl:{col,row}` 浮点写法内部用
      // `width*10000` 当"伪 EMU"算 nativeColOff，**不是真 EMU**——输出的 xdr:colOff 比期望小 ~7 倍，
      // 图位置会贴到单元格左上角（虽然不越界，但居中失效）。0908+ 直接用 nativeCol/nativeColOff
      // 写入真 EMU，确保所有 Office 客户端解析一致。
      const EMU_PER_PX = 9525;
      // 提前并发加载每张签名图拿原尺寸，按 maxH/maxW 等比缩放（不放大）
      const sigSizes: Array<{ w: number; h: number } | null> = hasSignature
        ? await Promise.all(exportRecords.map((r) => loadSignatureSizeConstrained(r.signature, SIG_MAX_W_PX, SIG_MAX_H_PX)))
        : [];
      // 附件题图片预加载（0904 多图改造，跟签名同理嵌入单元格）：fetch 附件 URL 拿 blob 转 base64。
      // ans 现在是数组 [{ attachment:true, name, type, size }]；逐张按 index 加载，存到 fileImgMap 用 `${r.id}:${k}:${idx}` 索引。
      // 兼容老单对象 ans（视为单元素数组 index=0）。仅 image/* 类型；非图片/失败走文本兜底。
      const fileImgMap = new Map<string, { base64: string; ext: string; size: { w: number; h: number } }>();
      await Promise.all(exportRecords.flatMap((r) => {
        if (!r.editToken) return [];
        return allKeys.filter((k) => metaFor(k)?.type === 'file').map(async (k) => {
          const raw = r.answers[k];
          const items: any[] = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
          await Promise.all(items.map(async (it, idx) => {
            if (!it || typeof it !== 'object' || it.attachment !== true) return;
            const type = it.type as string;
            if (!type || !type.startsWith('image/')) return;
            try {
              const url = idx > 0
                ? `/api/submissions/${r.editToken}/attachment/${k}?index=${idx}`
                : `/api/submissions/${r.editToken}/attachment/${k}`;
              const resp = await fetch(url);
              if (!resp.ok) return;
              const blob = await resp.blob();
              const dataUrl = await new Promise<string>((resolve) => {
                const fr = new FileReader();
                fr.onload = () => resolve(String(fr.result));
                fr.onerror = () => resolve('');
                fr.readAsDataURL(blob);
              });
              const m = dataUrl.match(/^data:image\/([\w+.-]+);base64,(.+)$/);
              if (!m) return;
              const size = await loadSignatureSizeConstrained(dataUrl, SIG_MAX_W_PX, SIG_MAX_H_PX);
              if (!size) return;
              let ext = m[1]; if (ext === 'jpeg') ext = 'jpg';
              fileImgMap.set(`${r.id}:${k}:${idx}`, { base64: m[2], ext, size });
            } catch { /* 失败走文本兜底 */ }
          }));
        });
      }));
      // ExcelJS 生成真 xlsx：签名 PNG 通过 addImage 嵌入单元格，Excel 原生显示
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('问卷数据', { properties: { defaultRowHeight: 18 } });
      const headers: string[] = ['序号', '问卷', '提交时间', ...(hasScore ? ['总分'] : []), ...exportCols.map((c) => c.label)];
      if (hasSignature) headers.push('电子签名');
      ws.columns = headers.map((h) => ({ header: h, width: 20, ...(mode === 'text' ? { style: { numFmt: '@' } } : {}) }));
      // 列头加粗
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getColumn(1).width = 8;
      // 提交时间列（第 3 列）格式化为文本
      ws.getColumn(3).numFmt = '@';
      // 签名列固定列宽（不自适应图片，保持表格原有行列尺寸）
      if (hasSignature) {
        ws.getColumn(headers.length).width = SIG_COL_WIDTH_CHARS;
      }
      // 逐行写入
      exportRecords.forEach((r, idx) => {
        const rowNum = idx + 2;
        const cells: any[] = [r.sequence ?? '', surveyTitle(r.surveyId), csvDate(r.createdAt)];
        if (hasScore) cells.push(r.score ?? '');
        const pendingImgs: Array<{ col: number; imgs: Array<{ base64: string; ext: string; size: { w: number; h: number } }> }> = [];
        for (const c of exportCols) {
          if (c.row !== undefined) {
            const rowVal = r.answers[c.key] && typeof r.answers[c.key] === 'object' ? (r.answers[c.key] as any)[c.row] : undefined;
            if (c.col !== undefined) {
              // 矩阵填空多列：文本列输出录入文字，下拉列输出选中的选项文本
              const t = matrixTextCellValue(rowVal, c.col, c.colIndex ?? 0);
              cells.push(t === '' ? '-' : t);
              continue;
            }
            // 矩阵滑动分数按数字写入，便于 Excel 统计
            if (typeof rowVal === 'number') { cells.push(rowVal); continue; }
            cells.push(rowVal === undefined || rowVal === null || rowVal === '' ? '-' : matrixTextRowText(rowVal));
            continue;
          }
          if (metaFor(c.key)?.type === 'file') {
            // 0904 多图：按 index 收集该 cell 全部图，按 idx 横向并列嵌入
            const raw = r.answers[c.key];
            const items: any[] = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
            const cellImgs = items
              .map((_, idx) => fileImgMap.get(`${r.id}:${c.key}:${idx}`))
              .filter((x): x is { base64: string; ext: string; size: { w: number; h: number } } => !!x);
            if (cellImgs.length > 0) {
              cells.push('');
              pendingImgs.push({ col: cells.length, imgs: cellImgs });
              continue;
            }
          }
          cells.push(formatAnswer(r.answers, c.key, mode, c.rankingOption, metaFor(c.key)));
        }
        if (hasSignature) cells.push(''); // 签名单元格先占位，下面 addImage
        // 序号、分数和各题型答案统一按模式写入；问卷名称、提交时间保持原文。
        ws.getRow(rowNum).values = cells.map((value, index) =>
          index === 1 || index === 2 ? value : exportCellValue(value, mode));
        ws.getRow(rowNum).alignment = { vertical: 'middle', wrapText: true };
        // 行高：默认 SIG_ROW_HEIGHT_PT；若该行含多张附件图，按图数 × 单图高度扩展（每图占 ~SIG_MAX_H_PX + 间距）
        let rowHeightPt = SIG_ROW_HEIGHT_PT;
        const maxImgsInRow = pendingImgs.reduce((acc, p) => Math.max(acc, p.imgs.length), 0);
        if (maxImgsInRow > 1) {
          rowHeightPt = Math.max(SIG_ROW_HEIGHT_PT, maxImgsInRow * (SIG_MAX_H_PX + 4) / 1.333);
        }
        ws.getRow(rowNum).height = rowHeightPt;
        // 嵌入附件图片（0904 多图：横向并列；等比缩放后居中放入单元格）
        for (const { col, imgs } of pendingImgs) {
          const colWidthPx = SIG_COL_WIDTH_CHARS * 7;
          const rowHeightPx = rowHeightPt * 1.333;
          let xCursor = 0; // 横向累计偏移 px
          for (const img of imgs) {
            try {
              const imgId = wb.addImage({ base64: img.base64, extension: img.ext as 'png' | 'jpg' | 'gif' });
              const offsetX = Math.max(0, (colWidthPx - img.size.w) / 2) + xCursor;
              const offsetY = Math.max(0, (rowHeightPx - img.size.h) / 2);
              ws.addImage(imgId, {
                // 用 nativeCol/nativeColOff 直接控制 EMU；ExcelJS 浮点 col/row 写法的内部单位换算
                // 跟 OOXML 标准不一致，导致 xdr:colOff/rowOff 不是真 EMU，部分客户端解析异常。
                tl: {
                  nativeCol: col - 1,
                  nativeColOff: Math.floor(offsetX * EMU_PER_PX),
                  nativeRow: rowNum - 1,
                  nativeRowOff: Math.floor(offsetY * EMU_PER_PX),
                },
                ext: { width: img.size.w, height: img.size.h },
                editAs: 'oneCell',
              });
              xCursor += img.size.w + 4;
            } catch (e) {
              console.error('附件图嵌入失败', e);
              ws.getRow(rowNum).getCell(col).value = '[附件图嵌入失败]';
            }
          }
        }
        // 嵌入签名图片到最后一列
        if (hasSignature) {
          const sigCol = headers.length; // 1-based
          const sig = r.signature;
          const size = sigSizes[idx];
          if (sig && size && /^data:image\/([\w+]+);base64,(.+)$/.test(sig)) {
            const match = sig.match(/^data:image\/([\w+]+);base64,(.+)$/)!;
            let ext = match[1];
            if (ext === 'jpeg') ext = 'jpg';
            const base64 = match[2];
            try {
              // ExcelJS 浏览器版用 base64 字段（不要求 Buffer）
              const imgId = wb.addImage({ base64, extension: ext as 'png' | 'jpg' | 'gif' });
              // 图片按缩放后的尺寸显示，居中放在单元格内（不撑大、不溢出）
              // 计算居中偏移：列方向 (colWidthPx - imgW)/2，行方向 (rowHeightPx - imgH)/2
              const colWidthPx = SIG_COL_WIDTH_CHARS * 7;
              const rowHeightPx = SIG_ROW_HEIGHT_PT * 1.333;
              const offsetX = Math.max(0, (colWidthPx - size.w) / 2);
              const offsetY = Math.max(0, (rowHeightPx - size.h) / 2);
              ws.addImage(imgId, {
                // 用 nativeCol/nativeColOff 直接写真 EMU；ExcelJS 的浮点 col/row 写法内部
                // 用 width*10000 当"伪 EMU"算 nativeColOff，跟 OOXML 标准 EMU 不一致，
                // 输出 xdr:colOff 比期望小 ~7 倍，图位置贴到单元格左上角（虽然不越界但居中失效）。
                tl: {
                  nativeCol: sigCol - 1,
                  nativeColOff: Math.floor(offsetX * EMU_PER_PX),
                  nativeRow: rowNum - 1,
                  nativeRowOff: Math.floor(offsetY * EMU_PER_PX),
                },
                ext: { width: size.w, height: size.h },
                editAs: 'oneCell',
              });
            } catch (e) {
              console.error('签名图嵌入失败', e);
              ws.getRow(rowNum).getCell(sigCol).value = '[签名图嵌入失败]';
            }
          } else if (sig && /^https?:\/\//.test(sig)) {
            ws.getRow(rowNum).getCell(sigCol).value = { text: '查看签名', hyperlink: sig };
          } else {
            ws.getRow(rowNum).getCell(sigCol).value = '-';
          }
        }
      });
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      // 文件名按模式区分：含签名图 = 现有；纯文本 / 按序号 加后缀，避免多个下载后分不清
      const suffix = mode === 'signed' ? '' : (mode === 'number' ? '-按序号' : '-按文本');
      // 0908 追加：文件名带当前问卷标题（去文件系统非法字符，长度截断 30 避免太长）；未筛选时退化为无标题
      const surveyNameRaw = surveyId ? (surveys.find((s) => s.id === surveyId)?.mainTitle ?? '').trim() : '';
      const safeSurveyName = surveyNameRaw.replace(/[\\/:*?"<>|\r\n\t]/g, '').slice(0, 30);
      const namePart = safeSurveyName ? `-${safeSurveyName}` : '';
      saveDownload(blob, `问卷数据${suffix}${namePart}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { window.alert(`导出失败：${actionError(e)}`); }
    finally { exportRunning.current = false; setExportProgress(''); }
  }

  return <AdminShell active="records" onLogout={onLogout}>
    <div className="page-heading"><div><div className="eyebrow">DATA CENTER · SUBMISSIONS</div><h1>数据记录</h1><p>查询和管理历史问卷填写数据</p></div>
      <div ref={exportMenuRef} style={{ position: 'relative', display: 'inline-flex' }}>
        <button className="btn btn-primary" onClick={() => void exportXls('signed')} disabled={loading || !total || !!exportProgress} aria-busy={!!exportProgress} title="导出当前筛选条件下全部页的记录（.xlsx），含签名图">{exportProgress || '↓ 导出当前结果（含签名图）'}</button>
        <button
          className="btn"
          aria-label="更多导出选项"
          aria-haspopup="menu"
          aria-expanded={exportMenuOpen}
          onClick={() => setExportMenuOpen((v) => !v)}
          disabled={loading || !total || !!exportProgress}
          style={{ marginLeft: -1, padding: '8px 10px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, color: exportMenuOpen ? 'var(--accent-strong)' : undefined }}
          title="更多导出格式"
        >▾</button>
        {exportMenuOpen && (
          <div role="menu" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60, minWidth: 220, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', boxShadow: '0 12px 32px rgba(15,23,42,.18)', padding: 6, display: 'grid', gap: 2 }}>
            <button
              role="menuitem"
              disabled={loading || !total || !!exportProgress}
              onClick={() => { setExportMenuOpen(false); exportXls('text'); }}
              style={{ textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: 'var(--text-1)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              title="选择题输出选项文字（含签名图）"
            >
              <div style={{ fontWeight: 500 }}>按文本下载 Excel</div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>选择题输出选项原文，含签名图</div>
            </button>
            <button
              role="menuitem"
              disabled={loading || !total || !!exportProgress}
              onClick={() => { setExportMenuOpen(false); exportXls('number'); }}
              style={{ textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: 'var(--text-1)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              title="选择题按选项位置映射成 1/2/3/4（非选择题按原文），含签名图"
            >
              <div style={{ fontWeight: 500 }}>按序号下载 Excel</div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>选项 ABCD 映射 1/2/3/4，含签名图</div>
            </button>
          </div>
        )}
      </div>
    </div>
    <div className="record-toolbar">
      <select className="records-folder-picker" aria-label="按项目文件夹筛选问卷" value={folderId} disabled={!surveysReady} onChange={event => { setSurveyId(''); setPage(0); setSearchParams(event.target.value ? { folder: event.target.value } : {}); }}>
        <option value="">所有文件夹</option><option value={UNFILED}>未归类</option>
        {folderData.items.map(folder => <option key={folder.id} value={folder.id}>{folderPath(folderData.items, folder.id)}</option>)}
        {folderId && folderId !== UNFILED && !folderData.items.some(folder => folder.id === folderId) && <option value={folderId}>文件夹已删除</option>}
      </select>
      <SurveyPicker surveys={surveys} value={surveyId} onChange={(id) => { setSurveyId(id); setPage(0); }} />
      <input placeholder="搜索填写内容" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchRecords()} />
      <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} /><span className="date-sep">至</span><input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
      <button className="btn" onClick={searchRecords} disabled={loading}>查询</button>
      {surveyId && <button className="btn" onClick={() => void downloadDocuments()} disabled={documentBusy} title="打包下载该问卷全部签署文档（不受当前筛选条件影响）">{documentBusy ? '文档加载中…' : '⇩ 批量下载签署文档'}</button>}
      {surveyId && <button className="btn" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => setPurgeOpen(true)} title="删除该问卷的全部提交数据（问卷保留）">清空该问卷数据</button>}
    </div>
    <div className="records-card"><div className="table-caption"><strong>提交记录</strong><span>共 {total} 条记录</span>{scoreStats && scoreStats.scored > 0 && <span style={{ marginLeft: 'auto', color: 'var(--accent-strong, #2f63d8)', fontWeight: 600 }}>评分统计：平均 {scoreStats.avg} 分 · 最低 {scoreStats.min} · 最高 {scoreStats.max}（{scoreStats.scored} 份计分）</span>}</div>
      {loading ? <div className="empty-state">正在加载数据…</div> : loadError ? <div className="empty-state" role="alert"><strong>{loadError}</strong></div> : !surveyId ? <div className="empty-state"><strong>暂无问卷</strong><p>创建问卷后，可在此查看提交记录。</p></div> : !records.length ? <div className="empty-state"><strong>暂无提交记录</strong><p>{appliedKeyword || from || to ? '当前筛选条件下没有匹配记录，请调整关键词或日期后重试。' : '目前还没有人通过填写链接提交问卷。请在问卷的“更多”菜单中选择“打开填写页”，复制链接发给填写人。'}</p></div> : <div className="table-scroll"><table><thead><tr><th scope="col" style={{ width: 64, whiteSpace: 'nowrap' }} title="按本问卷提交先后编号">序号</th><th>问卷</th>{columns.map((key) => { const t = titleFor(key); return <th key={key} className="th-truncate" title={t}>{t}</th>; })}<th>总分</th><th>提交时间</th><th>操作</th></tr></thead><tbody>{records.map((record) => <tr key={record.id}><td style={{ fontVariantNumeric: 'tabular-nums' }}>{record.sequence ?? '-'}</td><td><strong>{surveyTitle(record.surveyId)}</strong></td>{columns.map((key) => { const txt = cellText(record.answers, key); return <td key={key} className="answer-cell" title={txt}>{txt}</td>; })}<td>{record.score ?? '-'}</td><td className="muted">{dateText(record.createdAt)}</td><td><button className="text-button" disabled={detailLoading !== null} onClick={() => void showDetail(record.id)}>{detailLoading === record.id ? '加载中…' : '查看详情'}</button></td></tr>)}</tbody></table></div>}
      <RecordsPagination page={page} total={total} pageSize={pageSize} loading={loading} onChange={setPage} />
    </div>
    {selected && <div className="modal-backdrop" onClick={() => !busy && setSelected(null)}><div className="record-modal" onClick={(e) => e.stopPropagation()}><div className="modal-title"><div><div className="eyebrow">SUBMISSION #{selected.id}</div><h2>填写详情</h2></div><button className="modal-close" aria-label="关闭详情" disabled={busy} onClick={() => !busy && setSelected(null)}>×</button></div><div className="detail-meta"><span>序号：{selected.sequence ?? '-'}</span><span>问卷：{surveyTitle(selected.surveyId)}</span><span>提交时间：{dateText(selected.createdAt)}</span>{selected.score !== null && selected.score !== undefined && <span style={{ color: 'var(--accent-strong, #2f63d8)', fontWeight: 700 }}>总分：{selected.score}</span>}</div><div className="answer-detail">{Object.entries(selected.answers).filter(([key]) => key !== OPTION_TEXT_KEY).map(([key, value]) => <div className="answer-row" key={key}><span>{titleFor(key)}</span><strong style={{ whiteSpace: 'pre-line' }}>{(() => {
  // 0904 多图：附件题 value 是数组 [{ attachment:true, name, type, size }]；逐项给链接带 ?index=N
  const v = value as any;
  if (Array.isArray(v) && v.length > 0 && v[0]?.attachment === true) {
    return <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{v.map((it, idx) => <a key={idx} href={`/api/submissions/${selected.editToken}/attachment/${key}${idx > 0 ? `?index=${idx}` : ''}`} target="_blank" rel="noreferrer">{it.name ?? `附件 ${idx + 1}`} ↗</a>)}</span>;
  }
  if (v?.attachment === true) return <a href={`/api/submissions/${selected.editToken}/attachment/${key}`} target="_blank" rel="noreferrer">{v.name ?? '附件'} ↗</a>;
  if (Array.isArray(v?.images) && v.images.length > 0) return <>{String(v.text ?? '').trim()}<span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>{(v.images as string[]).map((src, i) => <img key={i} src={src} alt={`回答图片${i + 1}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border, #e5e7eb)' }} />)}</span></>;
  return cellText(selected.answers, key);
})()}</strong></div>)}{selected.signature && <div className="answer-row"><span>电子签名</span><a href={`/api/submissions/${selected.editToken}/signature`} target="_blank" rel="noreferrer">查看签名图片 ↗</a></div>}{selected.signature && <div className="answer-row"><span>签署文档</span><span><button className="text-button" disabled={documentBusy} onClick={() => void openDocument(false)}>查看 PDF ↗</button>　<button className="text-button" disabled={documentBusy} onClick={() => void openDocument(true)}>下载</button></span></div>}</div><div className="confirm-actions" style={{ marginTop: 16, justifyContent: 'flex-end' }}>{confirmDel ? <><button className="btn" onClick={() => setConfirmDel(false)} disabled={busy}>取消</button><button className="btn btn-primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => { void removeSelected(); }} disabled={busy}>{busy ? '删除中…' : '确认删除（连附件/签署文档）'}</button></> : <button className="btn" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => setConfirmDel(true)}>删除此记录</button>}</div></div></div>}
    {purgeOpen && <div className="modal-backdrop" onClick={() => !busy && setPurgeOpen(false)}><div className="record-modal submit-confirm" onClick={(e) => e.stopPropagation()}><div className="modal-title"><div><div className="eyebrow">PURGE SUBMISSIONS</div><h2>清空该问卷数据？</h2></div><button className="modal-close" aria-label="关闭清空确认" disabled={busy} onClick={() => setPurgeOpen(false)}>×</button></div><p>“{surveys.find((s) => s.id === surveyId)?.mainTitle ?? surveyId}”的全部提交记录、配额计数、附件与签署文档都将被删除，<b>不可撤销</b>。问卷本身保留，可继续收集。</p><div className="confirm-actions"><button className="btn" onClick={() => setPurgeOpen(false)} disabled={busy}>取消</button><button className="btn btn-primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => { void purgeCurrentSurvey(); }} disabled={busy}>{busy ? '清空中…' : '确认清空'}</button></div></div></div>}
  </AdminShell>;
}

export function AdminShell({ active, onLogout, children }: { active: 'dashboard' | 'records' | 'folders'; onLogout: () => void; children: ReactNode }) {
  const [loggingOut, setLoggingOut] = useState(false);
  const [recordsOpen, setRecordsOpen] = useState(active !== 'dashboard');
  async function logout() {
    if (loggingOut) return;
    setLoggingOut(true);
    try { await adminLogout(); onLogout(); }
    catch (e) { window.alert(`退出失败：${actionError(e)}`); }
    finally { setLoggingOut(false); }
  }
  return <div className={`admin-layout admin-polished-shell${active === 'dashboard' || active === 'folders' ? ' admin-dashboard-layout' : ''}${active === 'folders' ? ' admin-folder-layout' : ''}`}>
    <aside className="admin-sidebar"><div className="sidebar-brand"><span>问</span><strong>问卷管理</strong></div><div className="sidebar-label">工作台</div>
      <nav aria-label="后台导航">
        <Link className={active === 'dashboard' ? 'active' : ''} aria-current={active === 'dashboard' ? 'page' : undefined} to="/"><AdminIcon name="grid" />问卷管理</Link>
        <div className="records-nav-group"><button type="button" className={`records-nav-toggle${active !== 'dashboard' ? ' is-current' : ''}`} aria-expanded={recordsOpen} aria-controls="records-subnav" onClick={() => setRecordsOpen(value => !value)}><AdminIcon name="records" /><span>数据记录</span><AdminIcon name="chevron" className={recordsOpen ? 'is-expanded' : ''} /></button>
          {recordsOpen && <div id="records-subnav" className="records-subnav"><Link className={active === 'records' ? 'active' : ''} aria-current={active === 'records' ? 'page' : undefined} to="/records"><AdminIcon name="records" />提交记录</Link><Link className={active === 'folders' ? 'active' : ''} aria-current={active === 'folders' ? 'page' : undefined} to="/folders"><AdminIcon name="folder" />项目文件夹</Link></div>}
        </div>
      </nav><div className="sidebar-bottom"><AdminIcon name="user" /><div><strong>管理员</strong><button className="admin-logout" disabled={loggingOut} onClick={() => void logout()}>{loggingOut ? '退出中…' : '退出登录'}</button></div></div>
    </aside><main className="admin-main">{children}</main></div>;
}

/** 问卷下拉框内可输入搜索；选项按 mainTitle / subTitle / id 模糊过滤。 */
function SurveyPicker({ surveys, value, onChange }: { surveys: any[]; value: string; onChange: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  const current = surveys.find((s) => s.id === value);
  const label = value ? (current ? `${current.mainTitle ?? ''}${current.subTitle ? ` · ${current.subTitle}` : ''}` : value) : '请选择问卷';
  const q = filter.trim().toLowerCase();
  const list = q
    ? surveys.filter((s) => (s.mainTitle || '').toLowerCase().includes(q) || (s.subTitle || '').toLowerCase().includes(q) || (s.id || '').toLowerCase().includes(q))
    : surveys;
  const pick = (id: string) => { onChange(id); setOpen(false); setFilter(''); };
  return (
    <div className="survey-picker" ref={rootRef}>
      <button type="button" className={`survey-picker-trigger${open ? ' is-open' : ''}`} onClick={() => setOpen((o) => !o)} title="点击选择/搜索问卷">
        <span className="survey-picker-label">{label}</span>
        <span className="survey-picker-caret" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="survey-picker-panel">
          <input
            className="survey-picker-input"
            placeholder="搜索问卷（标题 / 副标题 / id）"
            value={filter}
            autoFocus
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && list.length > 0) pick(list[0].id); }}
          />
          <div className="survey-picker-list">
            {list.map((s) => (
              <button type="button" key={s.id} className={`survey-picker-item${value === s.id ? ' is-on' : ''}`} onClick={() => pick(s.id)}>
                <span className="survey-picker-item-title">{s.mainTitle || '未命名问卷'}</span>
                {s.subTitle && <span className="survey-picker-item-sub"> · {s.subTitle}</span>}
              </button>
            ))}
            {list.length === 0 && <div className="survey-picker-empty">未匹配到问卷</div>}
          </div>
        </div>
      )}
    </div>
  );
}
